/**
 * ScreenshotUpload Component
 *
 * Drag-and-drop file upload for screenshots with preview and validation.
 * Supports up to 3 images, 5MB each.
 *
 * @module components/feedback/ScreenshotUpload
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Grid,
  Alert,
  Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { VALIDATION_RULES } from '../../types/featureRequest';

interface ScreenshotUploadProps {
  screenshots: File[];
  onChange: (files: File[]) => void;
  maxFiles?: number;
  disabled?: boolean;
}

/**
 * ScreenshotUpload - Drag-and-drop file upload component
 */
const ScreenshotUpload: React.FC<ScreenshotUploadProps> = ({
  screenshots,
  onChange,
  maxFiles = VALIDATION_RULES.screenshots.maxCount,
  disabled = false,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Validate a file before adding
   */
  const validateFile = (file: File): string | null => {
    // Check file type
    if (!VALIDATION_RULES.screenshots.allowedTypes.includes(file.type)) {
      return `Invalid file type: ${file.type}. Please upload an image (JPEG, PNG, GIF, WebP).`;
    }

    // Check file size
    if (file.size > VALIDATION_RULES.screenshots.maxSizeBytes) {
      const maxMB = VALIDATION_RULES.screenshots.maxSizeBytes / (1024 * 1024);
      return `File "${file.name}" is too large. Maximum size is ${maxMB}MB.`;
    }

    return null;
  };

  /**
   * Generate preview URL for a file
   */
  const generatePreview = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  /**
   * Handle file selection
   */
  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      setError(null);

      // Check if adding these files would exceed the limit
      const currentCount = screenshots.length;
      const newCount = currentCount + files.length;

      if (newCount > maxFiles) {
        setError(`You can only upload up to ${maxFiles} screenshots. Please remove some first.`);
        return;
      }

      const validFiles: File[] = [];
      const newPreviews: string[] = [];

      // Validate and process each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const validationError = validateFile(file);

        if (validationError) {
          setError(validationError);
          return;
        }

        validFiles.push(file);

        // Generate preview
        try {
          const preview = await generatePreview(file);
          newPreviews.push(preview);
        } catch (err) {
          console.error('Error generating preview:', err);
        }
      }

      // Update state
      const updatedFiles = [...screenshots, ...validFiles];
      const updatedPreviews = [...previews, ...newPreviews];

      onChange(updatedFiles);
      setPreviews(updatedPreviews);
    },
    [screenshots, previews, maxFiles, onChange]
  );

  /**
   * Handle drag over
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragOver(true);
    }
  }, [disabled]);

  /**
   * Handle drag leave
   */
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  /**
   * Handle drop
   */
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      handleFiles(files);
    },
    [disabled, handleFiles]
  );

  /**
   * Handle file input change
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      handleFiles(files);
    },
    [handleFiles]
  );

  /**
   * Handle click on upload area
   */
  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  /**
   * Remove a file
   */
  const handleRemove = useCallback(
    (index: number) => {
      const updatedFiles = screenshots.filter((_, i) => i !== index);
      const updatedPreviews = previews.filter((_, i) => i !== index);

      onChange(updatedFiles);
      setPreviews(updatedPreviews);
      setError(null);
    },
    [screenshots, previews, onChange]
  );

  /**
   * Format file size for display
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const canUploadMore = screenshots.length < maxFiles;

  return (
    <Box>
      {/* Upload Area */}
      {canUploadMore && (
        <Paper
          elevation={dragOver ? 8 : 1}
          sx={{
            p: 3,
            border: dragOver ? '2px dashed' : '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'grey.300',
            bgcolor: dragOver ? 'action.hover' : 'background.paper',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            opacity: disabled ? 0.6 : 1,
            '&:hover': {
              borderColor: disabled ? 'grey.300' : 'primary.main',
              bgcolor: disabled ? 'background.paper' : 'action.hover',
            },
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1}
          >
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="h6" color="text.secondary">
              Drag & drop images here
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or click to browse
            </Typography>
            <Chip
              label={`Up to ${maxFiles} images, ${VALIDATION_RULES.screenshots.maxSizeBytes / (1024 * 1024)}MB each`}
              size="small"
              variant="outlined"
            />
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            accept={VALIDATION_RULES.screenshots.allowedTypes.join(',')}
            multiple
            onChange={handleInputChange}
            style={{ display: 'none' }}
            disabled={disabled}
          />
        </Paper>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Preview Grid */}
      {screenshots.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Screenshots ({screenshots.length}/{maxFiles})
          </Typography>
          <Grid container spacing={2}>
            {screenshots.map((file, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={2}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 1,
                  }}
                >
                  {/* Preview Image */}
                  {previews[index] ? (
                    <Box
                      component="img"
                      src={previews[index]}
                      alt={file.name}
                      sx={{
                        width: '100%',
                        height: 200,
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.100',
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 64, color: 'grey.400' }} />
                    </Box>
                  )}

                  {/* File Info */}
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography
                      variant="caption"
                      display="block"
                      noWrap
                      title={file.name}
                    >
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(file.size)}
                    </Typography>
                  </Box>

                  {/* Remove Button */}
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(index)}
                    disabled={disabled}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'background.paper',
                      '&:hover': {
                        bgcolor: 'error.light',
                        color: 'error.contrastText',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Help Text */}
      {screenshots.length === 0 && (
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Screenshots help us understand your request better. They are optional but highly
          recommended.
        </Typography>
      )}
    </Box>
  );
};

export default ScreenshotUpload;
